/* global paypal, braintree */
import logger from 'components/utils/logger'

// Initialise the Braintree-PayPal handshake and render the PayPal Smart Button(s).
//
// Behaviour by mode:
//   * Default (recurring vault) — single PayPal button rendered into
//     `#paypal-button-container`. flow:'vault', intent:'authorize'. Used by /checkout
//     subscribe and /review_order cart flows.
//   * options.enablePayLater === true — Lifetime checkout. Renders TWO buttons:
//     PayPal into `#paypal-paypal-button-container` and Pay Later into
//     `#paypal-paylater-button-container`. flow:'checkout', intent:'capture'. Pay Later
//     auto-hides itself when the buyer's region/amount don't qualify (PayPal-side eligibility),
//     so an empty container is harmless.
//
// The callback signature is unchanged from the previous v4 implementation:
//   isSubscribing → callback(nonce, order, plan, usedFreeTrial, userEmail)
//   else          → callback(nonce, order, deviceData)
export default (
  authorization,
  amount,
  callback,
  order,
  isSubscribing,
  plan,
  usedFreeTrial,
  deviceData,
  userEmail,
  options = {}
) =>
  braintree.client.create({ authorization }, (clientErr, clientInstance) => {
    if (clientErr) {
      logger.error(`Error creating client: ${clientErr}`)
      return
    }

    braintree.paypalCheckout.create(
      { client: clientInstance },
      (paypalCheckoutErr, paypalCheckoutInstance) => {
        if (paypalCheckoutErr) {
          logger.error(`Error creating PayPal Checkout: ${paypalCheckoutErr}`)
          return
        }

        const isLifetime = Boolean(options.enablePayLater)

        const fundingSources = isLifetime
          ? [paypal.FUNDING.PAYPAL, paypal.FUNDING.PAYLATER]
          : [paypal.FUNDING.PAYPAL]

        fundingSources.forEach((fundingSource) => {
          const containerSelector = isLifetime
            ? `#paypal-${
                fundingSource === paypal.FUNDING.PAYLATER ? 'paylater' : 'paypal'
              }-button-container`
            : '#paypal-button-container'

          paypal
            .Buttons({
              fundingSource,
              style: { shape: 'rect', layout: 'vertical' },
              createOrder: () =>
                paypalCheckoutInstance.createPayment({
                  flow: isLifetime ? 'checkout' : 'vault',
                  amount,
                  currency: 'USD',
                  intent: isLifetime ? 'capture' : 'authorize'
                }),
              onApprove: (data) =>
                paypalCheckoutInstance
                  .tokenizePayment(data)
                  .then((payload) => {
                    if (isSubscribing)
                      return callback(
                        payload.nonce,
                        order,
                        plan,
                        usedFreeTrial,
                        userEmail
                      )
                    return callback(payload.nonce, order, deviceData)
                  }),
              onError: (err) =>
                logger.error(
                  `PayPal button error (${String(fundingSource)}): ${err}`
                )
            })
            .render(containerSelector)
        })
      }
    )
  })
